import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { SkuInfo, skuDisplay } from '@/components/SkuInfo';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import { describeRequestError } from '@/lib/requests';
import { useDraftActions } from '@/lib/drafts';
import { RevisionInfo } from '@/components/RevisionInfo';

/**
 * One receiving request. A DRAFT is private to the seller: edit / send to work / delete. Once sent it is kept in the
 * history and can no longer be deleted; the seller can withdraw it only while the warehouse has not registered its
 * arrival.
 */
export function InboundDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const result = useApi(() => endpoints.inboundRequest(id!), [id]);
  const { t, locale } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  const [sentNow, setSentNow] = useState(false);
  const reload = result.reload;
  const draft = useDraftActions('INBOUND', (what) => {
    if (what === 'deleted') navigate('/inbound', { replace: true });
    else {
      setSentNow(true);
      reload();
    }
  });

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
  const { inbound, lines } = result.data;
  const revisions = result.data.revisions;
  const revisable = inbound.requested_in_portal && (inbound.can_revise ?? 'NONE') !== 'NONE';
  const sentToWarehouse = inbound.requested_in_portal && inbound.state !== 'DRAFT';
  const cancellable = inbound.requested_in_portal && inbound.state === 'EXPECTED';
  const isDraft = inbound.requested_in_portal && inbound.state === 'DRAFT';

  async function cancel(): Promise<void> {
    if (!window.confirm(t('requests.in.cancelConfirm'))) return;
    setBusy(true);
    setError(null);
    try {
      await endpoints.cancelInboundRequest(inbound.id);
      result.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('requests.in.detailTitle', { number: inbound.number })}</h1>
        <Link to="/inbound">{t('requests.in.back')}</Link>
      </div>
      <div className="card">
        <dl className="kv">
          <dt>{t('common.status')}</dt>
          <dd><StatusBadge value={inbound.state} /></dd>
          <dt>{t('requests.warehouse')}</dt>
          <dd>{inbound.warehouse_name}</dd>
          <dt>{t('requests.in.colCreated')}</dt>
          <dd>{formatDateTime(inbound.created_at, locale)}</dd>
          {inbound.expected_at && (
            <>
              <dt>{t('requests.in.date')}</dt>
              <dd>{formatDateTime(inbound.expected_at, locale)}</dd>
            </>
          )}
          <dt>{t('requests.in.colSource')}</dt>
          <dd>{inbound.requested_in_portal ? t('requests.in.sourcePortal') : t('requests.in.sourceWarehouse')}</dd>
        </dl>
        <p className="sub">{t(`requests.in.stateHint.${inbound.state}` as never)}</p>
        {sentToWarehouse && <RevisionInfo revisionNo={inbound.revision_no} lastEditedAt={inbound.last_edited_at} lastEditedBy={inbound.last_edited_by} revisions={revisions} />}
        {revisable && (
          <div className="form-actions">
            <Link className="button-link" to={`/inbound/${inbound.id}/revise`} data-testid="revise-action">{t('revise.action')}</Link>
          </div>
        )}
        {error !== null && <div className="error-banner" role="alert">{describeRequestError(t, error) ?? t('error.unexpected')}</div>}
        {draft.error !== null && <div className="error-banner" role="alert">{describeRequestError(t, draft.error) ?? t('drafts.err.generic')}</div>}
        {sentNow && !isDraft && <p className="order-done" role="status">{t('drafts.sentOk')}</p>}
        {isDraft && (
          <>
            <p className="kept-note">{t('drafts.hint')}</p>
            <div className="draft-actions">
              <button type="button" className="primary" disabled={draft.busyId !== null} onClick={() => void draft.send(inbound.id)}>
                {draft.busyId === inbound.id ? t('drafts.sending') : t('drafts.send')}
              </button>
              <Link className="button-link" to={`/inbound/${inbound.id}/edit`}>{t('drafts.edit')}</Link>
              <button type="button" className="danger" disabled={draft.busyId !== null} onClick={() => void draft.remove(inbound.id)}>
                {t('drafts.delete')}
              </button>
            </div>
          </>
        )}
        {cancellable && (
          <>
            <div className="form-actions">
              <button type="button" className="danger" disabled={busy} onClick={() => void cancel()}>
                {t('requests.in.cancel')}
              </button>
            </div>
            <p className="kept-note">{t('drafts.keptNote')}</p>
          </>
        )}
        {inbound.requested_in_portal && !isDraft && !cancellable && inbound.state !== 'CANCELLED' && <p className="kept-note">{t('drafts.keptNoteLate')}</p>}
      </div>
      <div className="card">
        <ul className="request-lines readonly">
          {lines.map((l) => (
            <li key={l.id}>
              <div className="rl-item"><SkuInfo sku={{ code: l.sku_code, name: l.sku_name, ...skuDisplay(l) }} /></div>
              <div className="rl-qty">
                {t('requests.in.expectedReceived', { expected: formatNumber(l.qty_expected, locale), received: formatNumber(l.qty_received, locale) })}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
